'use client';
import '/public/static/css/module/catch_list.scss';
import {useLocation, useSearchParams} from 'react-router-dom';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import classnames from 'classnames';
import {isEmpty} from 'lodash';
import {
  SoopUiProfileIconMenu,
  SoopUiImg,
  SoopUiContextMenu,
} from '@/components/ui';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import {HiddenStreamer} from '@/components/main/common';
import {useMixedContent} from '@/components/common/hooks/useMixedContent';
import globalDomains from '@/utils/global-domains';
import {decodeHtmlEntities} from '@/utils';
import {convertViewCnt, getCategoryThumbUrl, getValues} from '@/utils/main';
import {sendCatchClickLog, sendLegacyInflowPathLog} from '@/utils/main/my/log';
import {ROUTE} from '@/constants/main/route';
import useFavoriteStore from '@/stores/search';
import {isMobile} from 'react-device-detect';
import {DISPLAY_THUMBNAIL} from '@/constants/common/thumbnail';
import useGlobalUIStore from '@/stores';
import {BROADCAST_TYPES} from '@/constants';

/**
 * @typedef {Object} Menu
 * @description 캐치 컨텍스트 메뉴
 * @property {number} id - 메뉴 ID
 * @property {string} name - 메뉴 이름
 * @property {string} url - 메뉴 URL
 */

/**
 *
 * @param {Object} props - MyCatch 컴포넌트에 필요한 props
 * @param {boolean} props.isStory - 캐치 스토리 여부
 * @param {number} props.catchId - 캐치 ID (스토리도 동일하게 사용)
 * @param {string} props.thumbnailSrc - 썸네일 이미지 URL
 * @param {boolean} props.isHorizontal - 썸네일 이미지가 가로형인지 여부
 * @param {number} props.userId - BJ 사용자 ID
 * @param {string} props.userNickname - BJ 사용자 닉네임
 * @param {string} props.broadcastDate - 방송일시
 * @param {number} props.viewCount - 조회수
 * @param {string} props.title - 제목
 * @param {boolean} props.isAdult - 성인방송 여부
 * @param {Menu[]} props.catchContextMenuList - 캐치 컨텍스트 메뉴 리스트
 * @param {function} props.handleMenuClicked - 캐치 컨텍스트 메뉴 클릭 핸들러
 * @returns {JSX.Element} MyCatch 컴포넌트
 */
const MyCatch = React.memo(function MyCatch({
  isStory,
  catchId,
  thumbnailSrc,
  isHorizontal,
  userId,
  userNickname,
  broadcastDate,
  viewCount,
  title,
  isAdult,
  fileType = '',
  listDataType = '',
  originalUserId = '',
  originalUserNick = '',
  catchContextMenuList,
  hiddenStreamerList = [],
  handleUndoBroadcast = () => {},
  handleMenuClicked = () => {},
  userNick,
  isLogSendData = {},
  isSavygArchive = false,
  catchIdList = [],
  logCallback,
  grade,
  category,
  scheme = '',
  copyright = {},
  copyrightUserId,
  vodCategory,
  rank = null,
  hashTags = '',
  disableLinks = null, //링크이동 비활성화 옵션
}) {
  const location = useLocation();
  const {isDarkMode} = useGlobalUIStore();
  const [searchParams] = useSearchParams();
  const {toggleFavorite, setFavoriteStreamerName} = useFavoriteStore();

  const soopUiProfileIconMenu = useRef(null);

  const searchKey = searchParams.get('szKeyword');
  const storyRoute = getLocation(isStory, location.pathname);

  const formattedDate = () => {
    if (broadcastDate) {
      const broadDate = new Date(broadcastDate);
      return `${broadDate.getMonth() + 1}월 ${broadDate.getDate()}일`;
    }

    return '';
  };

  const originalAuthor =
    originalUserId || copyrightUserId || copyright?.userId || userId;

  const thumbnailImgUrl = useMemo(() => {
    if (isAdult) {
      return getCategoryThumbUrl(
        vodCategory,
        DISPLAY_THUMBNAIL.VERTICAL,
        'vod',
        isDarkMode,
      );
    } else {
      return thumbnailSrc;
    }
  }, [isAdult, vodCategory, isDarkMode, thumbnailSrc]);

  const handleMenuClickedWithData = (event, type, data) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClicked(event, type, data);
  };

  const generateCatchLink = (catchId) => {
    const baseUrl = `${globalDomains.VOD_AFREECATV}/player/${catchId}`;
    const isStoryType = isStory && fileType === 'CATCH_STORY';

    let path = isStoryType ? 'catchstory' : 'catch';

    // queryParams는 검색에서만 사용
    let queryParams = '';

    if (catchIdList.length > 0) {
      const catchIdIndex = catchIdList.indexOf(catchId);
      const relevantCatchIds = catchIdList
        .slice(catchIdIndex, catchIdIndex + 5) // 클릭한 영상부터 최대 5개만 선택
        .join('-');
      const relevantCatchStoryIds = catchIdList.slice(catchIdIndex).join('-');

      if (isStoryType) {
        queryParams = `?szLocation=${storyRoute}&aStoryListIdx=${relevantCatchStoryIds}`;
      } else {
        queryParams = `?szSearchTnoList=${relevantCatchIds}`;
      }
    }

    return `${baseUrl}/${path}${queryParams}`;
  };

  //스트리머 숨기기 컴포넌트
  if (hiddenStreamerList.includes(catchId)) {
    return (
      <HiddenStreamer
        type={'hidden'}
        userNickname={originalUserNick ? originalUserNick : userNickname}
        handleUndoBroadcast={() =>
          handleUndoBroadcast(
            {
              userNickname: originalUserNick ? originalUserNick : userNickname,
              userId: originalAuthor,
              broadcastId: catchId,
              listDataType,
            },
            'hidden',
          )
        }
      />
    );
  }

  const handleThumbnailClick = (type) => {
    // 캐치 유입 로그
    sendLegacyInflowPathLog({
      broadcastId: catchId,
      streamerId: originalAuthor,
      type: 'vod',
      moreData: {
        contentsType: 'vod',
        isStory,
        isCatch: true,
        listDataType,
        storyNo: isStory && catchId,
        ...(searchKey && {skey: searchKey}),
      },
      isLogSendData: {
        ...isLogSendData,
        uploader: userId,
      },
    });

    logCallback && logCallback(type);

    if (isStory) {
      return;
    }

    const myRoutes = getValues(ROUTE.MY);

    if (myRoutes.includes(location.pathname)) {
      sendCatchClickLog({
        streamerId: userId,
        catchId,
        fileType,
      });
    }
  };

  /**
   * 프로필 ict 메뉴의 이벤트가 발생했을 때 트리거
   * @param {EventData} event
   */
  const handleProfileIconMenuEvent = async (event) => {
    if (!event) return;

    const {eventName, data} = event;

    let param = eventName ? {type: eventName} : '';

    logCallback && logCallback(param);

    if (eventName === 'favorite') {
      toggleFavorite();
      setFavoriteStreamerName(data?.id);
    }
  };

  const isAdultThumbnail =
    isAdult ||
    (grade === '19' && (category === '30000' || category === '00030000'));

  if (grade === '19' && !thumbnailSrc) {
    thumbnailSrc = `${globalDomains.RES_AFREECATV}/images/main/catch_adult.png`;
  }

  return (
    <>
      <div className={classnames({story_thumb: isStory})}>
        <a
          data-url={thumbnailImgUrl}
          href={
            disableLinks ? 'javascript:void(0)' : generateCatchLink(catchId)
          }
          target={disableLinks ? '_self' : '_blank'}
          onClick={() => {
            if (typeof disableLinks === 'function') {
              disableLinks();
            }
            handleThumbnailClick({type: 'sn'});
          }}>
          {isStory && <span className='catch_story'>스토리</span>}
          {isAdult && <span className='status adult'>연령제한</span>}
          <Thumbnail
            thumbnailSrc={thumbnailImgUrl}
            rank={rank}
            isAdult={isAdult}
            isHorizontal={isHorizontal}
          />
        </a>
        {isStory && (
          <a
            href={generateCatchLink(catchId)}
            className='story_profile'
            target='_blank'>
            <MyProfileImg userId={userId} />
          </a>
        )}
      </div>
      <a
        href={disableLinks ? 'javascript:void(0)' : generateCatchLink(catchId)}
        className='info_area'
        target={disableLinks ? '_self' : '_blank'}
        title={title}>
        <p
          className='tit'
          onClick={(e) => {
            if (isStory) {
              e.preventDefault();
              soopUiProfileIconMenu.current?.open();
              logCallback && logCallback({type: 'nick'});
            } else {
              if (typeof disableLinks === 'function') {
                disableLinks();
              }
              logCallback && logCallback({type: 'tt'});
            }
          }}>
          {isStory ? userNickname : decodeHtmlEntities(title)}
        </p>

        <SoopUiProfileIconMenu
          ref={soopUiProfileIconMenu}
          isSavygArchive={isSavygArchive}
          streamerId={userId}
          streamerNick={userNickname}
          handleMenuButtonClicked={handleProfileIconMenuEvent}
        />
        {(viewCount || isStory) && (
          <p
            className={classnames({date: isStory, view: !isStory})}
            onClick={(e) => {
              logCallback && logCallback({type: 'tt'});
            }}>
            {isStory ? `${formattedDate()} 스토리` : convertViewCnt(viewCount)}
          </p>
        )}
        {!isEmpty(catchContextMenuList) && (
          <SoopUiContextMenu
            broadcastType={BROADCAST_TYPES.VOD}
            menuList={catchContextMenuList}
            handleMenuClicked={handleMenuClickedWithData}
            option={{
              title:
                isStory && fileType === 'CATCH_STORY'
                  ? `${formattedDate()} 스토리`
                  : title,
              thumbnailUrl: thumbnailSrc,
              userId: originalUserId ? originalUserId : userId,
              userNickname: originalUserNick || userNickname || userNick,
              broadcastId: catchId,
              appScheme: scheme,
              fileType,
              vodType: fileType,
              listDataType,
            }}
          />
        )}
      </a>
      {hashTags && disableLinks && (
        <div className='tag_wrap'>
          <span
            onClick={(e) => {
              e.preventDefault();
              if (isMobile) {
                window.open(`${globalDomains.BJ_AFREECATV}/${userId}`, '_self');
              } else {
                soopUiProfileIconMenu.current?.open();
              }
            }}>
            {hashTags}
          </span>
        </div>
      )}
    </>
  );
});

/**
 * Catch 컴포넌트의 하위 컴포넌트
 * @desc 컴포넌트 분리를 위해 이렇게 둠 (재사용은 되지 않아서 같은 파일 안에 뒀다)
 * @param {Object} props
 * @param {string} props.thumbnailSrc - 썸네일 src
 * @param {boolean} props.isAdult - 성인방송 여부
 * @param {boolean} props.isHorizontal - 가로 여부
 */
const Thumbnail = React.memo(function Thumbnail({
  rank,
  isAdult,
  isHorizontal,
  thumbnailSrc,
}) {
  const {imgSrc, loading, securedImageError} = useMixedContent(thumbnailSrc);
  const [isError, setIsError] = useState(false);

  const thumbClass = classnames('thumb', {
    thumb_16_9: isHorizontal && thumbnailSrc && !isAdult,
    'thumb-default': !thumbnailSrc || isError,
  });

  const handleImgError = () => {
    setIsError(true);
  };

  useEffect(() => {
    if (securedImageError) {
      setIsError(true);
    }
  }, [securedImageError]);

  if (loading) {
    return null;
  }

  return (
    <span className={thumbClass}>
      {thumbnailSrc && !isError && (
        <>
          <SoopUiImg src={imgSrc} onError={handleImgError} />
          {rank && <em className='rank'>{rank}</em>}
          {isHorizontal && (
            <div className='thumb_blur'>
              <SoopUiImg src={imgSrc} />
            </div>
          )}
        </>
      )}
    </span>
  );
});

function getLocation(isStory, pathname) {
  if (!isStory) {
    return;
  }
  if (ROUTE.INDEX === pathname) {
    return 'home';
  }
  if (ROUTE.SEARCH === pathname) {
    return 'search';
  }
  if (
    [
      ROUTE.MY.FAVORITE,
      ROUTE.MY.SUBSCRIBE,
      ROUTE.MY.FAN,
      ROUTE.MY.HISTORY.RECENT,
      ROUTE.MY.HISTORY.STORY,
    ].includes(pathname)
  ) {
    return 'my';
  }
  return '';
}

export default MyCatch;
